// Shared constants + styles for the Push notifications admin pages.

export const KIND_LABELS = {
  instant: 'Send now',
  scheduled: 'Once, later',
  recurring: 'Repeating',
  triggered: 'Automatic',
};

export const STATUS_COLORS = {
  draft: { bg: '#F3F4F6', fg: '#4B5563' },
  scheduled: { bg: '#DBEAFE', fg: '#1D4ED8' },
  sending: { bg: '#FEF3C7', fg: '#92400E' },
  active: { bg: '#D1FAE5', fg: '#065F46' },
  paused: { bg: '#FFEDD5', fg: '#9A3412' },
  completed: { bg: '#E0E7FF', fg: '#4338CA' },
  cancelled: { bg: '#FEE2E2', fg: '#991B1B' },
};
export const STATUSES = Object.keys(STATUS_COLORS);

export const CATEGORIES = [
  { id: 'reminders', label: 'Reminders', help: 'Deadlines, renewals, things the member asked us to remind them about.' },
  { id: 'updates', label: 'Updates', help: 'News about their own account: status changes, new documents, replies.' },
  { id: 'marketing', label: 'Offers & news', help: 'Promotions and announcements. Only goes to members who turned this on in the app (it is off by default).' },
  { id: 'security', label: 'Security', help: 'Sign-in and account safety alerts. Members cannot turn these off, so use it only for real security messages.' },
];

export const PRIORITIES = [
  { id: 'passive', label: 'Quiet', help: 'Arrives silently in Notification Center. No sound, does not light up the screen.' },
  { id: 'active', label: 'Normal', help: 'A regular notification: sound and banner. Use this for most messages.' },
  { id: 'time_sensitive', label: 'Time-sensitive', help: 'Breaks through Focus modes and quiet hours. Only for urgent things like a deadline today.' },
];

export const SOUNDS = [
  { id: 'default', label: 'Default iPhone sound' },
  { id: 'naytive', label: 'Naytive chime (coming soon, plays the default sound for now)' },
  { id: 'none', label: 'No sound' },
];

export const ACTIONS = [
  { id: 'open', title: 'Open' },
  { id: 'pay', title: 'Pay now' },
  { id: 'view', title: 'View' },
  { id: 'reply', title: 'Reply' },
  { id: 'later', title: 'Remind me tomorrow' },
  { id: 'done', title: 'Mark done' },
];

// Screens in the Naytive iOS app (naytive-app/src/app). Paths are expo-router routes.
// Screens in the redesigned app (naytive-app src/lib/links.ts SCREENS). The app
// also opens old links where they live now (/tasks -> Activity, the web
// dashboard's links), and a screen it does not know opens Activity.
// `param` entries need an id: picking one fills the link in with a <placeholder>
// to replace (the server refuses a link that still has one).
export const APP_ROUTES = [
  { path: '/', label: 'Home' },
  { path: '/activity', label: 'Activity (every push lands here too)' },
  { path: '/concierge', label: 'Concierge (Ask Naytive)' },
  { path: '/concierge?thread=<thread id>', label: 'A concierge / support thread', param: true },
  { path: '/explore', label: 'Explore' },
  { path: '/explore/for-you/<insight id>', label: 'A For-you insight', param: true },
  { path: '/insights', label: 'Insights' },
  { path: '/obligations', label: 'Obligations overview' },
  { path: '/business/deadlines', label: 'Obligations (company filings)' },
  { path: '/business/deadlines?company=<entity id>', label: 'Obligations for one company', param: true },
  { path: '/obligation/<obligation key>', label: 'One obligation', param: true },
  { path: '/obligation/<obligation key>?packet=1', label: 'One obligation, on "Check what we send"', param: true },
  { path: '/deadlines', label: 'Your deadlines (personal)' },
  { path: '/documents', label: 'Documents (vault)' },
  { path: '/documents/add', label: 'Add a passport or visa' },
  { path: '/business', label: 'Business' },
  { path: '/invoices', label: 'Invoices' },
  { path: '/invoices/new', label: 'New invoice' },
  { path: '/money', label: 'Money tab' },
  { path: '/money/account', label: 'Naytive account' },
  { path: '/money/account/<account id>/statements', label: 'Account statements', param: true },
  { path: '/money/apply', label: 'Open a Naytive account' },
  { path: '/money/cards', label: 'Cards' },
  { path: '/money/send', label: 'Send money' },
  { path: '/holdings', label: 'Net worth' },
  { path: '/connect', label: 'Connect accounts' },
  { path: '/connect/accounts', label: 'Connected accounts (reconnect)' },
  { path: '/connect/bank', label: 'Connect a bank' },
  { path: '/points', label: 'Rewards (points)' },
  { path: '/travel', label: 'Travel' },
  { path: '/days', label: 'Days' },
  { path: '/days?country=<country code>', label: 'Days, one country (e.g. AE, or schengen)', param: true },
  { path: '/countries', label: 'Every country that counts' },
  { path: '/residency', label: 'Residency' },
  { path: '/eligibility', label: 'Visa eligibility' },
  { path: '/esim', label: 'eSIM' },
  { path: '/search', label: 'Search' },
  { path: '/verify', label: 'Verify identity' },
  { path: '/personal', label: 'Personal details' },
  { path: '/profile', label: 'Profile (opened from the avatar)' },
  { path: '/profile/settings', label: 'Account settings' },
  { path: '/paywall', label: 'Upgrade (paywall)' },
];

// Variables only a link uses (filled per event; never shown in the copy).
export const LINK_VARS = ['entity_id', 'doc_link', 'conversation_id'];

export const COMMON_VARS = ['first_name', 'last_name', 'company_name', 'due_date', 'amount', 'visa_number'];

export const TRIGGERS = {
  'visa.status_changed': {
    label: 'Visa status changed',
    when: 'A member’s visa application moves to submitted, approved, rejected or more info needed.',
    vars: ['visa_number', 'status', 'country'],
  },
  'filing.due': {
    label: 'Filing due soon',
    when: 'A company filing is coming up (by default 7 days and 1 day before) or is overdue.',
    vars: ['filing_name', 'due_date', 'due_in', 'company_name', 'entity_id'],
    link: '/business/deadlines?company={{entity_id}}',
  },
  'payment.received': {
    label: 'Payment received',
    when: 'A payment from the member goes through (visa, incorporation, a deal milestone or a subscription).',
    vars: ['amount', 'item'],
  },
  'bank.disconnected': {
    label: 'Bank needs reconnecting',
    when: 'A linked bank or finance connection stops syncing and the member needs to reconnect it.',
    vars: ['institution'],
  },
  'document.requested': {
    label: 'Document requested',
    when: 'Our team asks the member to upload a document.',
    vars: ['document_name', 'doc_link'],
    link: '{{doc_link|/documents}}',
  },
  'concierge.reply': {
    label: 'New concierge reply',
    when: 'Someone (a person or the AI) replies in the member’s support chat while they are out of the app.',
    vars: ['preview', 'conversation_id'],
    link: '/concierge?thread={{conversation_id}}',
  },
  'passport.expiring': {
    label: 'Passport expiring',
    when: 'A passport on file is 6, 3 or 1 month from expiry, or has just expired.',
    vars: ['country', 'expires', 'passport_id', 'passport_source'],
    link: '/profile/vault/passports?open={{passport_id}}&source={{passport_source}}',
  },
};

// Sample values for the live preview.
export const SAMPLE_VARS = {
  first_name: 'Priya',
  last_name: 'Shah',
  company_name: 'Acme Labs Inc.',
  due_date: '15 Oct',
  due_in: 'in 7 days',
  amount: '$249.00',
  visa_number: 'NVMU2CR6D4HJ2',
  status: 'approved',
  country: 'India',
  filing_name: 'Delaware franchise tax',
  item: 'India e-Visa',
  institution: 'Chase',
  document_name: 'Passport (front page)',
  preview: 'Hi Priya, your documents look good. One quick question…',
  // Link-only variables: a test push opens the screen the real one would.
  entity_id: '',
  doc_link: '/documents',
  conversation_id: '',
};

// Sample values sent with test pushes. Member fields (name, company) are left
// out so the test member's real values are used.
const MEMBER_VARS = ['first_name', 'last_name', 'company_name'];
export const testSampleVars = () =>
  Object.fromEntries(Object.entries(SAMPLE_VARS).filter(([k]) => !MEMBER_VARS.includes(k)));

export const EngineErrorBanner = ({ err }) => (err ? (
  <div style={{ padding: '10px 14px', borderRadius: 10, background: '#FEE2E2', color: '#991B1B', fontSize: 13, marginBottom: 14 }}>
    <b>Paused by the system:</b> {err.error || 'the engine stopped this campaign'}{err.at ? ` (${fmtDate(err.at)})` : ''}.
    {' '}Fix the problem, then approve it again to restart.
  </div>
) : null);

export const fillVars = (s) =>
  String(s || '').replace(/\{\{\s*([a-z_]+)\s*\}\}/g, (m, k) => (SAMPLE_VARS[k] != null ? SAMPLE_VARS[k] : m));

export const WEEKDAYS = [
  { n: 1, l: 'Mon' }, { n: 2, l: 'Tue' }, { n: 3, l: 'Wed' }, { n: 4, l: 'Thu' },
  { n: 5, l: 'Fri' }, { n: 6, l: 'Sat' }, { n: 7, l: 'Sun' },
];

const COMMON_ZONES = ['Asia/Dubai', 'Asia/Kolkata', 'Europe/London', 'America/New_York', 'America/Los_Angeles', 'Asia/Singapore', 'UTC'];
export const TIMEZONES = (() => {
  let all = [];
  try { all = Intl.supportedValuesOf('timeZone'); } catch { all = []; }
  return [...COMMON_ZONES, ...all.filter((z) => !COMMON_ZONES.includes(z))];
})();

export function fmtDate(v) {
  if (!v) return '—';
  const d = new Date(typeof v === 'string' && !v.includes('T') && !v.endsWith('Z') ? `${v.replace(' ', 'T')}Z` : v);
  return Number.isNaN(d.getTime()) ? '—' : `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
}

export function describeSchedule(c) {
  if (c.kind === 'instant') return 'Immediately';
  if (c.kind === 'triggered') return TRIGGERS[c.trigger_key]?.label || c.trigger_key || '—';
  const where = c.local_time ? "member's local time" : c.timezone || '';
  if (c.kind === 'scheduled') return c.send_at_wall ? `${c.send_at_wall.replace('T', ' ')} (${where})` : '—';
  const r = c.recurrence_json || {};
  let s = r.freq === 'weekly'
    ? `Weekly on ${(r.weekdays || []).map((n) => WEEKDAYS[n - 1]?.l).join(', ')}`
    : r.freq === 'monthly' ? `Monthly on day ${r.monthday}` : 'Daily';
  s += ` at ${r.time || '—'} (${where})`;
  if (r.ends_at) s += `, until ${r.ends_at}`;
  if (r.max_sends) s += `, ${r.max_sends} times max`;
  return s;
}

export function describeAudience(a) {
  if (!a) return '—';
  if (a.mode === 'all') return 'All members';
  if (a.mode === 'segment') return `Segment (${Object.keys(a.segment || {}).length} filter${Object.keys(a.segment || {}).length === 1 ? '' : 's'})`;
  const n = (a.user_ids || []).length + (a.emails || []).length;
  return `${a.mode === 'test' ? 'Test devices' : 'Specific members'} (${n})`;
}

// ── styles (match NaytiveCareers / NaytiveBlog) ──
export const S = {
  btnPrimary: { padding: '8px 16px', border: 'none', borderRadius: 8, background: '#2540D9', color: '#fff', fontSize: 13, fontWeight: 500, cursor: 'pointer' },
  btnGhost: { padding: '8px 14px', border: '1px solid #D1D5DB', borderRadius: 8, background: '#fff', color: '#374151', fontSize: 13, fontWeight: 500, cursor: 'pointer' },
  btnDanger: { padding: '8px 14px', border: '1px solid #FCA5A5', borderRadius: 8, background: '#fff', color: '#B91C1C', fontSize: 13, fontWeight: 500, cursor: 'pointer' },
  card: { border: '1px solid #E5E7EB', borderRadius: 12, overflow: 'hidden', background: '#fff' },
  section: { border: '1px solid #E5E7EB', borderRadius: 12, background: '#fff', padding: 16, marginBottom: 14 },
  sectionTitle: { fontSize: 14, fontWeight: 600, color: '#111827', margin: '0 0 4px' },
  sectionHelp: { fontSize: 12.5, color: '#6B7280', margin: '0 0 12px' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  thead: { background: '#F9FAFB', textAlign: 'left', color: '#6B7280' },
  th: { padding: '11px 14px', fontSize: 11, fontWeight: 600, letterSpacing: 0.3, textTransform: 'uppercase' },
  td: { padding: '12px 14px', color: '#374151', verticalAlign: 'top' },
  pill: { display: 'inline-block', fontSize: 11, fontWeight: 600, padding: '2px 9px', borderRadius: 999, textTransform: 'capitalize' },
  mini: { fontSize: 12, padding: '4px 9px', border: '1px solid #E5E7EB', borderRadius: 6, background: '#fff', color: '#374151', cursor: 'pointer' },
  empty: { padding: 40, textAlign: 'center', color: '#9CA3AF', fontSize: 13, border: '1px solid #E5E7EB', borderRadius: 12, background: '#fff' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.4)', display: 'grid', placeItems: 'center', zIndex: 100, padding: 16 },
  modal: { background: '#fff', borderRadius: 14, padding: 22, boxShadow: '0 24px 60px rgba(0,0,0,0.2)', maxHeight: '88vh', overflowY: 'auto' },
  lbl: { display: 'block', fontSize: 11.5, fontWeight: 500, color: '#6B7280', margin: '0 0 4px' },
  inp: { width: '100%', boxSizing: 'border-box', padding: '8px 11px', border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', outline: 'none', background: '#fff' },
  help: { fontSize: 12, color: '#6B7280', marginTop: 4, lineHeight: 1.45 },
  chip: { fontSize: 12, padding: '3px 9px', border: '1px solid #C7D2FE', borderRadius: 999, background: '#EEF2FF', color: '#3730A3', cursor: 'pointer' },
  toast: { position: 'fixed', bottom: 26, left: '50%', transform: 'translateX(-50%)', background: '#111827', color: '#fff', padding: '10px 18px', borderRadius: 10, fontSize: 13, zIndex: 200 },
  banner: { padding: '10px 14px', borderRadius: 10, background: '#FEF3C7', color: '#92400E', fontSize: 13, marginBottom: 14 },
};

export const StatusPill = ({ status }) => (
  <span style={{ ...S.pill, background: (STATUS_COLORS[status] || {}).bg, color: (STATUS_COLORS[status] || {}).fg }}>{status}</span>
);
